import {
  orange,
  blue,
  green,
  red,
  grey,
  yellow,
  lightGreen,
  pink,
  purple,
  deepPurple,
  deepOrange,
  indigo,
  cyan,
  amber,
  teal,
  common,
} from '@mui/material/colors'

import T from 'T'

// Mirrors DraftConfiguredServiceService.MAX_CONTAINER_QUANTITY; the backend rejects a draft above it.
export const MAX_CONTAINER_QUANTITY = 100

export const SIGNATURE_REQUIRED_OPTIONS = ['Signature Only', 'Damage Waiver', 'Certificate of Destruction']
export const SKILLS_REQUIRED_OPTIONS = ['Military Clearance', 'Security Clearance']

// Mirrors crm-common StopType.kt.
export const STOP_TYPE = {
  WORK_ORDER: 'WORK_ORDER',
  DISPOSAL: 'DISPOSAL',
}

export const WORK_ORDER_STATUS = {
  SERVICED: 'Serviced',
  SKIPPED: 'Skipped',
  IN_PROGRESS: 'In Process',
  IN_PROCESS: 'In Process',
  SCHEDULED: 'Scheduled',
  COMPLETE: 'Complete',
  CANCELED: 'Canceled',
  HOLD: 'Hold',
  PARTIAL: 'Partial',
  SUSPENDED: 'Suspended',
  // Roll-ups of a WO's per-container completion. Mobile 2.0 also writes them into the legacy work_order_status.
  MIXED: 'Mixed',
  INCOMPLETE: 'Incomplete',
  MIXED_INCOMPLETE: 'Mixed Incomplete',
  // Display-only: what the Mixed / Mixed Incomplete / Incomplete roll-ups read as.
  PARTIALLY_SERVICED: 'Partially Serviced',
  PARTIALLY_SKIPPED: 'Partially Skipped',
  // A hold the account's vacation drives, as opposed to one applied to this work order.
  VACATION_HOLD: 'Vacation Hold',
}

export const WO_STATUS_BE_TO_FE_FORMAT: Record<string, string> = {
  SERVICED: 'Serviced',
  SKIPPED: 'Skipped',
  INPROGRESS: 'In Process',
  INPROCESS: 'In Process',
  SCHEDULED: 'Scheduled',
  COMPLETE: 'Complete',
  CANCELED: 'Canceled',
  HOLD: 'Hold',
  PARTIAL: 'Partial',
  SUSPENDED: 'Suspended',
  // WorkOrderCompletionRollupStatus, folded by WorkOrderStatusEnricher; the legacy column holds it too.
  MIXED: 'Partially Serviced',
  MIXEDINCOMPLETE: 'Partially Serviced',
  INCOMPLETE: 'Partially Skipped',
}

export const normalizeWorkOrderStatusKey = (raw?: unknown): string => (raw ? String(raw).replace(/[\s_]/g, '').toUpperCase() : '')

export const getWorkOrderStatusDisplayLabel = (raw?: unknown, fallback = ''): string => {
  if (!raw) return fallback
  const key = normalizeWorkOrderStatusKey(raw)
  return WO_STATUS_BE_TO_FE_FORMAT[key] ?? fallback
}

export const EVENT_INSTANCE_TYPES = {
  DAY_OF_WEEK: 'DayOfWeek',
  SPECIFIC_DATE: 'SpecificDate',
}

export const CONFIGURED_SERVICE_EVENT_TYPE = {
  ONE_OFF: 'one off',
  RECURRING: 'recurring',
}

const {
  SERVICED,
  VACATION_HOLD,
  SKIPPED,
  IN_PROCESS,
  SCHEDULED,
  CANCELED,
  HOLD,
  PARTIAL,
  SUSPENDED,
  MIXED,
  INCOMPLETE,
  MIXED_INCOMPLETE,
  PARTIALLY_SERVICED,
  PARTIALLY_SKIPPED,
} = WORK_ORDER_STATUS
const { white, black } = common

// Mixed is a verdict on every can; Incomplete and Mixed Incomplete still have one waiting.
export const TERMINAL_WORK_ORDER_STATUSES = [SERVICED, SKIPPED, CANCELED, MIXED]
// The driver's own verdicts — terminal minus Canceled, which ends a stop without anyone touching it.
export const COMPLETED_WORK_ORDER_STATUSES = [SERVICED, SKIPPED, MIXED]

const PARTIALLY_SERVICED_DISPLAY = { type: 'success', bgColor: green[50], borderColor: green[200] }
const PARTIALLY_SKIPPED_DISPLAY = { type: 'warning', bgColor: orange[50], borderColor: orange[200] }

type WorkOrderStatusDisplay = { type: string; bgColor: string; borderColor: string; marker?: string; markerTextColor?: string }

export const WORK_ORDER_STATUS_DISPLAY: Record<string, WorkOrderStatusDisplay> = {
  [SERVICED]: { type: 'success', bgColor: green[100], borderColor: green[200], marker: 'serviced-marker', markerTextColor: white },
  [SKIPPED]: { type: 'warning', bgColor: orange[100], borderColor: orange[200], marker: 'skipped-marker', markerTextColor: black },
  [IN_PROCESS]: { type: 'info', bgColor: blue[100], borderColor: blue[200], marker: 'in-progress-marker', markerTextColor: white },
  [SCHEDULED]: { type: '', bgColor: grey[100], borderColor: grey[400], marker: 'scheduled-marker', markerTextColor: white },
  [CANCELED]: { type: 'error', bgColor: red[100], borderColor: red[200], marker: 'canceled-marker', markerTextColor: white },
  [HOLD]: { type: '', bgColor: deepPurple[100], borderColor: deepPurple[200], marker: 'hold-marker', markerTextColor: white },
  [VACATION_HOLD]: { type: '', bgColor: deepPurple[100], borderColor: deepPurple[200] },
  [PARTIAL]: { type: 'warning', bgColor: yellow[100], borderColor: yellow[400], marker: 'partial-marker', markerTextColor: black },
  [SUSPENDED]: { type: 'error', bgColor: red[200], borderColor: red[300], marker: 'suspended-marker', markerTextColor: white },
  [PARTIALLY_SERVICED]: PARTIALLY_SERVICED_DISPLAY,
  [PARTIALLY_SKIPPED]: PARTIALLY_SKIPPED_DISPLAY,
  // Pins reuse registered Mapbox images by the completion rule: Mixed is done, the other two still have a can waiting.
  [MIXED]: { ...PARTIALLY_SERVICED_DISPLAY, marker: 'serviced-marker', markerTextColor: white },
  [MIXED_INCOMPLETE]: { ...PARTIALLY_SERVICED_DISPLAY, marker: 'in-progress-marker', markerTextColor: white },
  [INCOMPLETE]: { ...PARTIALLY_SKIPPED_DISPLAY, marker: 'in-progress-marker', markerTextColor: white },
}

export const PRICED_SERVICE_TYPE = {
  RECURRING: 'Recurring',
  ON_REQUEST: 'On Request',
}

export const PLAN_TYPE = {
  INSTANCE: 'Instance',
  RECURRING: 'Recurring',
}

export const RECURRING_COLLECTION = 'Recurring collection'
export const RECURRING_COLLECTION_ID = 'RECURRING COLLECTION'

export const SERVICE_ACTION_TYPE = {
  SWAP: 'Swap',
  DELIVERY: 'Delivery',
  'DUMP & RETURN': 'Dump & Return',
  DROP: 'Drop',
  'LIVE LOAD': 'Live Load',
  REPOSITION: 'Reposition',
  FINAL: 'Final',
  COLLECTION: 'Collection',
  RENTAL: 'Rental',
  CONCIERGE: 'Concierge',
  REPAIR: 'Repair',
  'DUMP & RELOCATE': 'Dump & Relocate',
  COURTESY: 'Courtesy',
  INSTALL: 'Install',
  'BIN CLEANING': 'Bin Cleaning',
  SALES: 'Sales',
  PUMP: 'Pump',
  'DRY RUN': 'Dry Run',
}

export const SERVICE_ACTION_TYPE_TO_COLOR_MAP = {
  SWAP: yellow,
  DELIVERY: deepOrange,
  'DUMP & RETURN': indigo,
  DROP: orange,
  'LIVE LOAD': lightGreen,
  REPOSITION: red,
  FINAL: pink,
  COLLECTION: purple,
  RENTAL: green,
  CONCIERGE: blue,
  REPAIR: blue,
  'DUMP & RELOCATE': deepPurple,
  COURTESY: cyan,
  INSTALL: lightGreen,
  'BIN CLEANING': amber,
  SALES: teal,
  PUMP: deepOrange,
  'DRY RUN': grey,
}

export const SERVICE_VIEW = {
  WEEK: 'Week',
  UPCOMING: 'Upcoming',
}

export const SERVICE_TYPE = {
  SERVICE: 'Service',
  SERVICE_GROUP: 'Service Group',
}

export const RECURRENCE = {
  RECURRING: 'Recurring',
  ON_REQUEST: 'On Request',
}

export const RECURRENCE_V2 = {
  RECURRING: 'RECURRING',
  ON_REQUEST: 'ON_REQUEST',
}

const { RECURRING, ON_REQUEST } = RECURRENCE

export const RECURRENCE_LABELS = {
  [RECURRING]: 'Recurring',
  [ON_REQUEST]: 'On-request',
}

export const HOLD_TYPES = {
  HOLD: 'Hold',
  VACATION: 'Vacation',
  SUSPENSION: 'Suspension',
} as const

export const HOLD_FORM_MODES = {
  CREATE: 'create',
  UPDATE: 'update',
} as const

export const WORK_ORDER_COLUMNS = [T.PREVIOUS_WO, T.PREVIOUS_DATE, T.NEW_DATE, T.ROUTE]

export const SERVICE_STATUS = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
}
